import { Chip, Tooltip } from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import PauseCircleOutlined from '@mui/icons-material/PauseCircleOutlined';
import DraftsOutlined from '@mui/icons-material/DraftsOutlined';
import { ArrayItemRendererWithCutOff } from 'components/ArrayItemRendererWithCutOff';
import { integrationsHash } from '@constants/integration';
import { TeamSearchIntegration } from 'TeamModule/api/searchTeamsByFieldsApi';
import { DataCellProps } from '../types';

const ALLOWED_STATES = new Set(['DRAFT', 'ACTIVE', 'SUSPENDED']);

const STATE_CHIP_CONFIG: Record<string, { icon: JSX.Element; color: string; tooltip: string }> = {
  ACTIVE: {
    icon: <CheckCircleOutlined sx={{ fontSize: 14 }} />,
    color: '#4CAF50',
    tooltip: 'Active',
  },
  SUSPENDED: {
    icon: <PauseCircleOutlined sx={{ fontSize: 14 }} />,
    color: '#FF9800',
    tooltip: 'Suspended',
  },
  DRAFT: {
    icon: <DraftsOutlined sx={{ fontSize: 14 }} />,
    color: '#2196F3',
    tooltip: 'Draft',
  },
};

interface IntegrationChipItem {
  targetService: string;
  label: string;
  state?: string;
}

const TeamsV2IntegrationsCountCell = ({ team }: DataCellProps) => {
  const rawIntegrations = team.integrations || [];
  if (!rawIntegrations.length) return null;

  // Build chip items, only including Draft, Active, and Suspended integrations
  const items: IntegrationChipItem[] = rawIntegrations
    .filter((i) => {
      if (typeof i === 'string') return true;
      return ALLOWED_STATES.has((i as TeamSearchIntegration).state);
    })
    .map((i) => {
      if (typeof i === 'string') {
        return {
          targetService: i,
          label: integrationsHash[i]?.label || i,
        };
      }
      const integration = i as TeamSearchIntegration;
      return {
        targetService: integration.targetService,
        label: integrationsHash[integration.targetService]?.label || integration.targetService,
        state: integration.state,
      };
    });

  return (
    <ArrayItemRendererWithCutOff<IntegrationChipItem>
      cutOffLimit={0}
      items={items}
      renderItem={({ item }) => {
        const stateConfig = item.state ? STATE_CHIP_CONFIG[item.state] : undefined;
        const chip = (
          <Chip
            label={item.label}
            size="small"
            icon={stateConfig?.icon}
            variant="outlined"
            sx={{
              mr: 0.5,
              mb: 0.25,
              borderColor: stateConfig?.color ?? undefined,
              color: stateConfig?.color ?? undefined,
              '& .MuiChip-icon': stateConfig?.color ? { color: stateConfig.color } : undefined,
            }}
          />
        );
        return stateConfig ? (
          <Tooltip title={stateConfig.tooltip} key={item.targetService}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      }}
      renderRemainingItems={({ remainingItems }) => (
        <Chip label={`+ ${remainingItems.length}`} size="small" sx={{ mr: 0.5 }} />
      )}
      getKey={(item) => `${item.targetService}-${item.state || ''}`}
    />
  );
};

export default TeamsV2IntegrationsCountCell;
